import React from 'react';
import { X, Edit, MapPin, Tag, Leaf, AlertTriangle, Camera, Share2 } from 'lucide-react';

const FoodModal = ({ food, isOpen, onClose, onEdit }) => {
  if (!isOpen || !food) return null;

  const isFreeFood = food.protein_exchanges < 1.0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{food.name}</h2>
              {food.brand && (
                <p className="text-lg text-gray-600">{food.brand}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(food)}
                className="btn btn-outline btn-sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Badges */}
            <div className="flex flex-wrap gap-2">
              {isFreeFood && (
                <span className="badge badge-success">
                  <Leaf className="w-3 h-3 mr-1" />
                  Free Food
                </span>
              )}
              {food.contains_aspartame && (
                <span className="badge badge-warning">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Contains Aspartame
                </span>
              )}
              <span className="badge badge-secondary">
                {food.protein_exchanges} exchanges
              </span>
            </div>

            {/* Main Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Basic Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Description */}
                {food.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-600">{food.description}</p>
                  </div>
                )}

                {/* Nutrition Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Nutrition Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Protein exchanges:</span>
                        <span className="font-medium">{food.protein_exchanges}</span>
                      </div>
                      {food.protein_per_100g && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Protein per 100g:</span>
                          <span className="font-medium">{food.protein_per_100g}g</span>
                        </div>
                      )}
                      {food.serving_size && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Serving size:</span>
                          <span className="font-medium">{food.serving_size}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Free food:</span>
                        <span className={isFreeFood ? 'text-green-600 font-medium' : 'text-gray-900'}>
                          {isFreeFood ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {food.contains_aspartame && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Aspartame:</span>
                          <span className="text-red-600 font-medium">
                            Contains
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ingredients */}
                {food.ingredients && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Ingredients</h3>
                    <p className="text-gray-600">{food.ingredients}</p>
                  </div>
                )}

                {/* Additional Nutrition Info */}
                {food.nutrition_facts && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Additional Information</h3>
                    <p className="text-gray-600">{food.nutrition_facts}</p>
                  </div>
                )}

                {/* Barcode */}
                {food.barcode && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Barcode</h3>
                    <p className="text-gray-600 font-mono">{food.barcode}</p>
                  </div>
                )}
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* Exchange Circle */}
                <div className="text-center">
                  <div 
                    className={`w-24 h-24 rounded-full flex flex-col items-center justify-center mx-auto mb-3 ${
                      isFreeFood 
                        ? 'bg-green-100 border-4 border-green-300' 
                        : 'bg-blue-100 border-4 border-blue-300'
                    }`}
                  >
                    <span className={`text-2xl font-bold ${
                      isFreeFood ? 'text-green-800' : 'text-blue-800'
                    }`}>
                      {food.protein_exchanges}
                    </span>
                    <span className={`text-sm ${
                      isFreeFood ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      exchanges
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {isFreeFood ? 'Free food - less than 1g protein' : 'Protein exchange value'}
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="space-y-3">
                  <button className="btn btn-primary w-full">
                    Add to My Foods
                  </button>
                  <button className="btn btn-outline w-full">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </button>
                </div>

                {/* Food Stats */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Food Stats</h4>
                  <div className="space-y-2 text-sm">
                    {food.created_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Added:</span>
                        <span className="text-gray-900">
                          {new Date(food.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {food.updated_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Updated:</span>
                        <span className="text-gray-900">
                          {new Date(food.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Photos */}
            {food.photo_urls && food.photo_urls.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {food.photo_urls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`${food.name} photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Location and Type Labels */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Locations */}
              {food.locations && food.locations.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Locations
                  </h3>
                  <div className="space-y-2">
                    {food.locations.map((location, index) => (
                      <span 
                        key={`location-${index}`}
                        className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mr-2 mb-2"
                      >
                        {location.name || location}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Types */}
              {food.types && food.types.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Tag className="w-5 h-5 mr-2" />
                    Food Types
                  </h3>
                  <div className="space-y-2">
                    {food.types.map((type, index) => (
                      <span 
                        key={`type-${index}`}
                        className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mr-2 mb-2"
                      >
                        {type.name || type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Store Locations */}
            {food.store_locations && food.store_locations.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Where to Buy</h3>
                <div className="space-y-3">
                  {food.store_locations.map((location, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">{location.store_name}</div>
                          <div className="text-sm text-gray-500">
                            {location.city}{location.state && `, ${location.state}`}
                          </div>
                        </div>
                      </div>
                      {location.price && (
                        <div className="text-lg font-semibold text-gray-900">
                          ${location.price}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodModal;
